import { Component, ElementRef, HostListener, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouterOutlet, RouterLink, RouterLinkActive, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs/operators';
import { SeoService, FaqItem, HowToStep } from './seo.service';
import { AuthService } from './auth/core/auth.service';
import { ARTICLES } from './pages/learn/learn-content';
import { environment } from '../environments/environment';

export interface NavItem {
  path:  string;
  label: string;
  icon:  string;
}

export interface NavCategory {
  key:   string;
  label: string;
  icon:  string;
  items: NavItem[];
}

const NAV_CATEGORIES: NavCategory[] = [
  {
    key: 'home-loan', label: 'Home Loan Calculators', icon: 'home', items: [
      { path: '/home-loan',            label: 'Home Loan EMI',        icon: 'home' },
      { path: '/home-loan-prepayment', label: 'Home Loan Prepayment', icon: 'payments' },
      { path: '/stamp-duty-karnataka', label: 'Karnataka Stamp Duty', icon: 'gavel' },
      { path: '/tds-property-sale',    label: 'TDS on Property Sale (Form 141/26QB)', icon: 'home_work' }
    ]
  },
  {
    key: 'loan', label: 'Loan Calculators', icon: 'account_balance', items: [
      { path: '/personal-loan', label: 'Personal Loan EMI', icon: 'credit_card' },
      { path: '/car-loan',      label: 'Car Loan EMI',      icon: 'directions_car' }
    ]
  },
  {
    key: 'deposits', label: 'Deposits & Savings', icon: 'savings', items: [
      { path: '/ppf', label: 'PPF', icon: 'account_balance' },
      { path: '/ssy', label: 'SSY', icon: 'child_care' },
      { path: '/fd',  label: 'FD',  icon: 'savings' },
      { path: '/rd',  label: 'RD',  icon: 'autorenew' }
    ]
  },
  {
    key: 'retirement', label: 'Retirement Planning', icon: 'elderly', items: [
      { path: '/epf',  label: 'EPF',  icon: 'apartment' },
      { path: '/nps',  label: 'NPS',  icon: 'elderly' },
      { path: '/fire', label: 'FIRE', icon: 'local_fire_department' }
    ]
  },
  {
    key: 'mutual-fund', label: 'Mutual Fund Calculators', icon: 'trending_up', items: [
      { path: '/lumpsum',     label: 'Lumpsum',     icon: 'monetization_on' },
      { path: '/sip',         label: 'SIP',         icon: 'trending_up' },
      { path: '/step-up-sip', label: 'Step-Up SIP', icon: 'stairs' },
      { path: '/sip-vs-step-up-sip', label: 'SIP vs Step-Up SIP', icon: 'compare_arrows' },
      { path: '/stp',         label: 'STP',         icon: 'swap_horiz' },
      { path: '/swp',         label: 'SWP',         icon: 'call_made' }
    ]
  },
  {
    key: 'growth-tools', label: 'Growth & Planning Tools', icon: 'insights', items: [
      { path: '/cagr',      label: 'CAGR',      icon: 'percent' },
      { path: '/inflation', label: 'Inflation', icon: 'price_change' }
    ]
  },
  {
    key: 'tax-insurance', label: 'Tax & Insurance', icon: 'receipt_long', items: [
      { path: '/term-insurance', label: 'Term Insurance', icon: 'security' },
      { path: '/income-tax',     label: 'Income Tax',     icon: 'receipt_long' },
      { path: '/hra',            label: 'HRA Exemption',  icon: 'location_city' }
    ]
  }
];

const NAV_SERVICES: NavItem[] = [
  { path: '/reminders',       label: 'Reminders',                  icon: 'notifications' },
  { path: '/budget',          label: 'Budget & Expense Tracker',   icon: 'account_balance_wallet' },
  { path: '/todo',            label: 'To-Do',                      icon: 'checklist' },
  // RMS (Rental Management System) nav entry gated behind environment.features.rmsEnabled
  // (see PENDING_FEATURES.md #1) - mirrors the /rms routes' own gate in app.routes.ts.
  ...(environment.features.rmsEnabled
    ? [{ path: '/rms', label: 'Rental Management System', icon: 'home_work' }]
    : []),
  // Rental Agreement Generator nav entry gated behind environment.features.rentalAgreementEnabled
  // (see PENDING_FEATURES.md #3) - mirrors the /rental-agreement route's own gate in app.routes.ts.
  ...(environment.features.rentalAgreementEnabled
    ? [{ path: '/rental-agreement', label: 'Rental Agreement Generator', icon: 'description' }]
    : [])
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, MatIconModule],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.scss']
})
export class App implements OnInit {
  private router = inject(Router);
  private seo = inject(SeoService);
  private elRef = inject(ElementRef);
  private doc = inject<Document>(DOCUMENT);

  private readonly authService = inject(AuthService);

  readonly currentYear = new Date().getFullYear();
  readonly navCategories = NAV_CATEGORIES;
  readonly navServices = NAV_SERVICES;
  readonly articles = ARTICLES;
  readonly footerArticles = ARTICLES.slice(0, 5);
  readonly isPremiumLoggedIn = this.authService.isAuthenticated;
  readonly currentUser = this.authService.currentUser;

  mobileMenuOpen = false;
  calcMenuOpen = false;
  servicesMenuOpen = false;
  accountMenuOpen = false;
  activeCategory: string | null = null;

  ngOnInit(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        let snapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
        while (snapshot.firstChild) snapshot = snapshot.firstChild;
        const description = snapshot.data['description'] as string | undefined;
        const canonicalUrl = this.toCanonicalUrl(e.urlAfterRedirects);
        if (description) this.seo.updateDescription(description);
        if (snapshot.title) this.seo.updateTitleTags(snapshot.title);
        this.seo.updateCanonical(canonicalUrl);
        if (snapshot.title && description) {
          const name = snapshot.title.replace(' | Every Rupee Spent', '').split(' - ')[0];
          if (snapshot.data['schemaType'] === 'service') {
            this.seo.updateServiceJsonLd({
              name,
              description,
              url: canonicalUrl,
              price: snapshot.data['price'],
              priceCurrency: snapshot.data['priceCurrency'],
              areaServed: snapshot.data['areaServed']
            });
          } else if (snapshot.data['schemaType'] === 'article') {
            this.seo.updateArticleJsonLd({
              headline: snapshot.title.replace(' | Every Rupee Spent', ''),
              description,
              url: canonicalUrl,
              datePublished: snapshot.data['datePublished']
            });
          } else {
            this.seo.updateSoftwareAppJsonLd({ name, description, url: canonicalUrl });
          }
        }
        this.updateBreadcrumbs(e.urlAfterRedirects, snapshot.title, canonicalUrl);
        const howToName = snapshot.data['howToName'] as string | undefined;
        const howToSteps = (snapshot.data['howToSteps'] as HowToStep[] | undefined) ?? [];
        this.seo.updateHowToJsonLd(howToName ?? '', howToSteps);

        this.seo.trackPageview(e.urlAfterRedirects);
        this.closeMenus();

        // Route-supplied FAQ data (e.g. /learn articles) is preferred over scraping the DOM,
        // because dynamically-rendered (@for-driven) FAQ markup hasn't painted into the DOM yet
        // at this point in the navigation — only static, hand-written FAQ markup has.
        const routeFaqItems = snapshot.data['faqItems'] as FaqItem[] | undefined;
        const faqItems = routeFaqItems ?? Array.from(this.doc.querySelectorAll('.faq-item'))
          .map(el => ({
            question: el.querySelector('h3')?.textContent?.trim() ?? '',
            answer: el.querySelector('p')?.textContent?.trim() ?? ''
          }))
          .filter(item => item.question && item.answer);
        this.seo.updateFaqJsonLd(faqItems);
      });
  }

  // The site is served from S3, which 302s a path without a trailing slash to
  // the slash version. Canonical tags must point straight at the URL that
  // actually returns 200, regardless of which form was navigated to.
  private toCanonicalUrl(urlAfterRedirects: string): string {
    const [pathAndQuery, fragment] = urlAfterRedirects.split('#');
    const [path, query] = pathAndQuery.split('?');
    const normalizedPath = path === '/' ? '/' : `${path.replace(/\/+$/, '')}/`;
    return `https://everyrupeespent.com${normalizedPath}${query ? '?' + query : ''}${fragment ? '#' + fragment : ''}`;
  }

  // Section-level crumb labels for the small set of routes with a natural parent hub -
  // everything else is a flat top-level route, so Home > Page Title is the full breadcrumb.
  private static readonly SECTION_LABELS: Record<string, string> = {
    learn: 'Money Guides for India'
  };

  private updateBreadcrumbs(urlAfterRedirects: string, pageTitle: string | undefined, canonicalUrl: string): void {
    const segments = urlAfterRedirects.split('?')[0].split('/').filter(Boolean);
    if (segments.length === 0 || !pageTitle) {
      this.seo.updateBreadcrumbJsonLd([]);
      return;
    }

    const items = [{ name: 'Home', url: 'https://everyrupeespent.com/' }];
    const sectionLabel = App.SECTION_LABELS[segments[0]];
    if (sectionLabel && segments.length > 1) {
      items.push({ name: sectionLabel, url: `https://everyrupeespent.com/${segments[0]}/` });
    }
    items.push({ name: pageTitle.replace(' | Every Rupee Spent', ''), url: canonicalUrl });
    this.seo.updateBreadcrumbJsonLd(items);
  }

  private categoryForCurrentRoute(): string | null {
    const url = this.router.url;
    const category = this.navCategories.find(c =>
      c.items.some(i => url === i.path || url.startsWith(`${i.path}?`))
    );
    return category ? category.key : null;
  }

  isCalculatorRouteActive(): boolean {
    return this.categoryForCurrentRoute() !== null;
  }

  isServiceRouteActive(): boolean {
    const url = this.router.url;
    return this.navServices.some(s => url === s.path || url.startsWith(`${s.path}/`) || url.startsWith(`${s.path}?`));
  }

  isCategoryActive(categoryKey: string): boolean {
    return this.categoryForCurrentRoute() === categoryKey;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.calcMenuOpen = false;
    this.servicesMenuOpen = false;
    this.accountMenuOpen = false;
    this.activeCategory = null;
  }

  toggleCalcMenu(event: Event): void {
    event.stopPropagation();
    this.calcMenuOpen = !this.calcMenuOpen;
    this.servicesMenuOpen = false;
    this.accountMenuOpen = false;
    this.activeCategory = this.calcMenuOpen ? this.categoryForCurrentRoute() : null;
  }

  toggleServicesMenu(event: Event): void {
    event.stopPropagation();
    this.servicesMenuOpen = !this.servicesMenuOpen;
    this.calcMenuOpen = false;
    this.accountMenuOpen = false;
    this.activeCategory = null;
  }

  toggleAccountMenu(event: Event): void {
    event.stopPropagation();
    this.accountMenuOpen = !this.accountMenuOpen;
    this.calcMenuOpen = false;
    this.servicesMenuOpen = false;
    this.activeCategory = null;
    if (this.accountMenuOpen && !this.currentUser()) {
      this.authService.loadCurrentUser().subscribe();
    }
  }

  logout(): void {
    this.authService.logout();
    this.closeMenus();
    this.router.navigate(['/login']);
  }

  openCategory(event: Event, categoryKey: string): void {
    event.stopPropagation();
    this.activeCategory = categoryKey;
  }

  backToCategories(event: Event): void {
    event.stopPropagation();
    this.activeCategory = null;
  }

  closeMenus(): void {
    this.mobileMenuOpen = false;
    this.calcMenuOpen = false;
    this.servicesMenuOpen = false;
    this.accountMenuOpen = false;
    this.activeCategory = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node;
    const dropdowns = (this.elRef.nativeElement as HTMLElement).querySelectorAll('.nav-dropdown');
    const clickedInsideDropdown = Array.from(dropdowns).some(el => el.contains(target));
    if (!clickedInsideDropdown) {
      this.calcMenuOpen = false;
      this.servicesMenuOpen = false;
      this.accountMenuOpen = false;
      this.activeCategory = null;
    }
  }
}
